/**
 * Role icons
 *
 * One canonical lucide icon per account/role kind. Used by the account
 * switcher (login + portal), the staff Users page, and the /signup
 * student-vs-parent picker — keep these in sync so the same role reads
 * the same wherever it appears.
 */

import { Apple, BadgeCheck, Flag, Backpack, Home } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type RoleIconKind =
  | 'teacher'
  | 'siteLeader'
  | 'admin'
  | 'student'
  | 'parent';

// Staff icons (Apple/BadgeCheck/Flag) match the existing convention in
// UserDetailPage so the same role reads the same wherever it appears.
// Student/Parent are new and not previously iconified.
export const ROLE_ICON: Record<RoleIconKind, LucideIcon> = {
  teacher: Apple,
  siteLeader: Flag,
  admin: BadgeCheck,
  student: Backpack,
  parent: Home,
};

/**
 * Normalize any of the staff-role display strings we ship today
 * ("Administrators" | "Admin" | "Site Leader" | "Teacher" | ...) to the
 * canonical icon kind. Unknown strings fall back to teacher — the safest
 * baseline since most users carry that role.
 */
export function staffRoleIconKind(role: string): RoleIconKind {
  switch (role) {
    case 'Administrators':
    case 'Admin':
      return 'admin';
    case 'Site Leader':
      return 'siteLeader';
    case 'Teacher':
    default:
      return 'teacher';
  }
}
